import { cfetch, BadStatusError } from '@confluence/network';

import {
	isValidSnippetMetadataResponse,
	type SnippetMetadataPayload,
	type SnippetMetadataResponse,
} from './types/conversationalAiClientTypes.ts';

const COMMON_HEADERS = {
	'Content-Type': 'application/json',
	Accept: 'application/json',
	'X-Experience-Id': 'confluence-snippets-metadata',
	'X-Product': 'confluence',
} as const;

// Convo-AI path, served by conversational-ai-platform. This is the permanent,
// unconditional route for snippet metadata.
//
// It used to be the control arm of the cc-smarts snippet metadata migration
// experiment (removed in FFCLEANUP-189148; the gate key is intentionally not
// repeated here so stale-flag tooling stays clean), which would have moved
// these requests to cc-smarts. That experiment never started: it stayed in
// setup in Switcheroo with no launched group and an empty final value, so
// every production request resolved here — 876 non-synthetic Convo-AI
// requests vs 0 to cc-smarts over a six-hour sample. It was therefore removed
// rather than shipped, and because the cc-smarts route was never exercised,
// re-attempting that migration means re-introducing the endpoint under a new
// experiment rather than restoring this one.
const CONVO_AI_URL = '/gateway/api/assist/api/confluence/v1/snippets/metadata';

export class SnippetMetadataError extends Error {
	statusCode: number | undefined;

	constructor(message: string, statusCode?: number) {
		super(message);
		this.name = 'SnippetMetadataError';
		this.statusCode = statusCode;
	}
}

/**
 * Fetches AI-generated name and description suggestions for a snippet.
 *
 * Requests are served by conversational-ai-platform.
 *
 * Caller must pass a SnippetMetadataPayload (e.g. { adfContent: JSON.stringify(adf) })
 * and cloudId from session data for the request to succeed.
 */
export const fetchSnippetMetadata = async (
	snippetMetadataPayload: SnippetMetadataPayload,
	cloudId: string,
): Promise<SnippetMetadataResponse> => {
	let response: Response;
	try {
		response = await cfetch(CONVO_AI_URL, {
			method: 'POST',
			headers: {
				...COMMON_HEADERS,
				'X-Cloudid': cloudId,
			},
			body: JSON.stringify(snippetMetadataPayload),
		});
	} catch (err) {
		if (BadStatusError.isBadStatusError(err)) {
			throw new SnippetMetadataError(
				`Snippet metadata request failed: ${err.response.statusText}`,
				err.response.status,
			);
		}
		throw err;
	}

	const data: SnippetMetadataResponse = await response.json();
	if (!isValidSnippetMetadataResponse(data)) {
		throw new SnippetMetadataError('Snippet metadata request failed: Invalid response');
	}
	return data;
};
